import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    BodyText,
    Button,
    CopyToClipboard,
    Divider,
    FlexContainer,
    Modal,
    Spinner,
} from '@placer-ui/components';
import {
    selectOfflineReportsAccountRequests,
    selectOfflineReportsClosedRequests,
    selectOfflineReportsOpenRequests,
    selectOfflineReportsRequestsComments,
    selectOfflineReportsRequestsCommentsLoading,
    selectOfflineReportsSharedWithMeRequests,
} from 'store/offline-reports/selectors/offline-reports-selectors-factory';

import { TitleModalHeader } from 'features/my-zone/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import styles from './xtra-request-conversation-modal.module.scss';
import { OrderManagementRequest } from 'core/entities';
import { useGetUsersMap } from '../../hooks/use-get-xtra-requests-data';
import {
    convertXtraRequestStatus,
    getRequestedByUser,
    getUserReportDates,
    getUserReportRequestedOnDates,
    parseHtmlComment,
} from '../../utils/utils';
import { XtraRequestComment } from '../xtra-request-comment/xtra-request-comment';
import { useOpenSupportRequest } from 'features/my-zone/support-requests/components/support-requests-modals/support-modal-manager';

export type XtraConversationRequestProps = {
    requestId: string;
};

export const XtraRequestConversationModal = ({ requestId }: XtraConversationRequestProps) => {
    const usersMap = useGetUsersMap();
    const openRequests = useSelector(selectOfflineReportsOpenRequests);
    const closedRequests = useSelector(selectOfflineReportsClosedRequests);
    const sharedWithMeRequests = useSelector(selectOfflineReportsSharedWithMeRequests);
    const accountRequests = useSelector(selectOfflineReportsAccountRequests);
    const { openSupportRequest } = useOpenSupportRequest();
    const selectedRequest: OrderManagementRequest | undefined = useMemo(() => {
        if (!requestId) return undefined;

        for (const requestsList of [openRequests, closedRequests, sharedWithMeRequests]) {
            const selectedReq = requestsList.find(
                (request) => Number(request.id) === Number(requestId),
            );

            if (selectedReq) return selectedReq;
        }

        return accountRequests.find((request) => Number(request.id) === Number(requestId));
    }, [requestId, openRequests, closedRequests, sharedWithMeRequests, accountRequests]);

    const {
        getOfflineReportsRequestsComments,
        clearOfflineReportsRequestComments,
        setOfflineReportsRequestAsRead,
    } = useOfflineReportsActions();
    const comments = useSelector(selectOfflineReportsRequestsComments);
    const isLoading = useSelector(selectOfflineReportsRequestsCommentsLoading);
    const firstCommentData = useMemo(() => {
        return parseHtmlComment(comments[0]?.html_body);
    }, [comments]);

    useEffect(() => {
        if (!requestId) return;

        getOfflineReportsRequestsComments(requestId);
        setOfflineReportsRequestAsRead(requestId);
    }, [requestId, getOfflineReportsRequestsComments, setOfflineReportsRequestAsRead]);

    useEffect(
        () => () => {
            clearOfflineReportsRequestComments();
        },
        [clearOfflineReportsRequestComments],
    );

    if (!selectedRequest) return null;
    const { status, requestedOn, subject, sharedBy, reportDates, requestorEmail } = selectedRequest;

    return (
        <>
            <TitleModalHeader title={subject} />
            <Modal.Content>
                <div className={styles.modalBody}>
                    <div className={styles.info}>
                        <BodyText variant="bold">Placer.ai support ticket #:</BodyText>
                        <FlexContainer>
                            <CopyToClipboard testId="xtra-copy-id-to-clipboard">
                                {requestId as string}
                            </CopyToClipboard>
                        </FlexContainer>
                        <BodyText variant="bold">Report ordered by:</BodyText>
                        <BodyText testId="xtra-modal-ordered-by">
                            {getRequestedByUser(usersMap, sharedBy, requestorEmail)}
                        </BodyText>
                        <BodyText variant="bold">Report Dates:</BodyText>
                        <BodyText testId="xtra-modal-report-dates">
                            {getUserReportDates(reportDates)}
                        </BodyText>
                        <BodyText variant="bold">Requested On:</BodyText>
                        <BodyText testId="xtra-modal-requested-on">
                            {getUserReportRequestedOnDates(requestedOn)}
                        </BodyText>
                        <BodyText variant="bold">Report Status:</BodyText>
                        <BodyText testId="xtra-modal-status">
                            {convertXtraRequestStatus(status)}
                        </BodyText>
                        {firstCommentData.map((item, index) => (
                            <>
                                <BodyText variant="bold" key={`${index}-0`}>
                                    {index !== 0 &&
                                    firstCommentData[index - 1].headline === item.headline
                                        ? ''
                                        : item.headline}
                                </BodyText>
                                <BodyText testId={`xtra-modal-comment-${index}`} key={`${index}-1`}>
                                    {item.value}
                                </BodyText>
                            </>
                        ))}
                    </div>
                    <Divider />

                    {isLoading ? (
                        <FlexContainer justifyContent="center" alignItems="center" height="260px">
                            <Spinner variant="brand" />
                        </FlexContainer>
                    ) : (
                        <FlexContainer gap={16} flexDirection="column" minHeight="270px">
                            {[...comments]
                                .reverse()
                                .map(
                                    (comment, index) =>
                                        index !== comments.length - 1 && (
                                            <XtraRequestComment
                                                comment={comment}
                                                key={comment.id}
                                            />
                                        ),
                                )}
                        </FlexContainer>
                    )}
                </div>
            </Modal.Content>
            {status !== 'closed' && (
                <Modal.Footer>
                    <Modal.Footer.RightControls>
                        <Button
                            testId="xtra-request-conversation-modal-submit-button"
                            variant="primary-brand"
                            fullWidth
                            onClick={() =>
                                openSupportRequest({
                                    flowType: 'xtraMessage',
                                    props: { selectedRequest: selectedRequest },
                                })
                            }
                        >
                            Send a Message
                        </Button>
                    </Modal.Footer.RightControls>
                </Modal.Footer>
            )}
        </>
    );
};
